import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { describeSuggestion } from "@/components/admin/internal-rate-format";
import { MonthSelect } from "@/components/shared/MonthSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAddResourceInternalRate } from "@/hooks/use-resource-internal-rates";
import { currentMonth, monthsAroundToday } from "@/lib/month-options";
import { formatPlanMoney } from "@/lib/plan-format";
import type { ResourceInternalRateRowView } from "@/schemas/resource-internal-rates";

const schema = z.object({
  internalCost: z
    .string()
    .trim()
    .regex(/^\d{1,8}(\.\d{1,2})?$/, "Enter an hourly rate like 95 or 95.50."),
  effectiveMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Pick the month the rate starts."),
  note: z.string().max(500).optional(),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ResourceInternalRateRowView;
  stepUpId: string;
}

/** Add an Internal Rate for one Resource, from a chosen month on. */
export function InternalRateDialog({ open, onOpenChange, row, stepUpId }: Props) {
  const add = useAddResourceInternalRate(stepUpId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { internalCost: "", effectiveMonth: currentMonth(), note: "" },
  });
  const suggestion = row.suggestion;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Set Internal Rate — {row.resourceName}</DialogTitle>
          <DialogDescription>
            The rate applies from the first of the month you pick until a later rate starts.
            {row.currentRate !== null &&
              ` Currently ${formatPlanMoney(row.currentRate.internalCost)}/hr.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit((values) =>
              add.mutate(
                {
                  resourceId: row.resourceId,
                  internalCost: values.internalCost,
                  effectiveMonth: values.effectiveMonth,
                  note: values.note,
                },
                { onSuccess: () => onOpenChange(false) },
              ),
            )}
          >
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">
                    Suggested:{" "}
                    {suggestion.kind === "suggested"
                      ? `${formatPlanMoney(suggestion.internalCost)}/hr`
                      : "none"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {describeSuggestion(suggestion)}
                  </span>
                </div>
                {suggestion.kind === "suggested" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      form.setValue("internalCost", suggestion.internalCost, {
                        shouldValidate: true,
                      })
                    }
                  >
                    <Sparkles className="size-4" aria-hidden />
                    Use suggestion
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="internalCost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate per hour (USD)</FormLabel>
                    <FormControl>
                      <Input inputMode="decimal" placeholder="95.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="effectiveMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Starts</FormLabel>
                    <FormControl>
                      <MonthSelect
                        value={field.value}
                        options={monthsAroundToday({
                          back: 12,
                          ahead: 24,
                          selected: field.value,
                        })}
                        onValueChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note (optional)</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="e.g. Annual review" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {row.livePlanCount > 0 && (
              <p className="flex items-start gap-2 text-sm text-warning-950 dark:text-warning-400">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {row.resourceName} is named on {row.livePlanCount} live Resource Plan
                {row.livePlanCount === 1 ? "" : "s"}. A plan's margin uses Internal Rates only once
                it names at least two Resources that have one; those plans change for the months
                this rate covers.
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={add.isPending}>
                {add.isPending ? "Saving…" : "Save rate"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
